/**
 * Building blocks for API schemas: IDs, timestamps, pagination, validated text fields.
 * IDs are UUIDv7 strings, timestamps in the API are ISO 8601 UTC strings (ADR 017).
 */
import { z } from 'zod';
import {
  ACTIVATION_CODE_INPUT_MAX_LENGTH,
  DISPLAY_NAME_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  LEARNINGSUITE_GROUP_ID_MAX_LENGTH,
  NAME_MAX_LENGTH,
  PAGE_SIZE_DEFAULT,
  PAGE_SIZE_MAX,
  REASON_MAX_LENGTH,
  SEARCH_MAX_LENGTH,
} from '../constants';
import { normalizeEmail } from '../utils/email';
import { containsControlCharacters } from '../utils/text';

export const IdSchema = z.uuid();
export const IdParamsSchema = z.object({ id: IdSchema });
export type IdParams = z.infer<typeof IdParamsSchema>;

/** ISO 8601 UTC timestamp, e.g. `2026-10-01T12:00:00.000Z`. */
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });
export const NullableIsoDateTimeSchema = IsoDateTimeSchema.nullable();

/** Opaque token from links (verification, magic link). base64url, ≥ 256 bit. */
export const OpaqueTokenSchema = z
  .string()
  .min(43)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/u, 'Ungültiges Token-Format');

/** Raw activation code as typed/scanned (normalised and validated by the server, §14). */
export const ActivationCodeInputSchema = z.string().min(1).max(ACTIVATION_CODE_INPUT_MAX_LENGTH);

/** Trimmed person name: not empty, max length, no control characters (§15). */
export const PersonNameSchema = z
  .string()
  .trim()
  .min(1, 'Bitte ausfüllen.')
  .max(NAME_MAX_LENGTH, `Maximal ${NAME_MAX_LENGTH} Zeichen.`)
  .refine((v) => !containsControlCharacters(v), 'Enthält unzulässige Zeichen.');

/** Generic single-line text with control-character check. */
export function singleLineText(maxLength: number) {
  return z
    .string()
    .trim()
    .min(1, 'Bitte ausfüllen.')
    .max(maxLength, `Maximal ${maxLength} Zeichen.`)
    .refine((v) => !containsControlCharacters(v), 'Enthält unzulässige Zeichen.');
}

export const DisplayNameSchema = singleLineText(DISPLAY_NAME_MAX_LENGTH);
export const ReasonSchema = singleLineText(REASON_MAX_LENGTH);

/** Email input: syntax check, length limit, normalised (trim + lowercase) on parse (§15). */
export const EmailInputSchema = z
  .string()
  .trim()
  .max(EMAIL_MAX_LENGTH, `Maximal ${EMAIL_MAX_LENGTH} Zeichen.`)
  .pipe(z.email('Bitte geben Sie eine gültige E-Mail-Adresse ein.'))
  .transform(normalizeEmail);

/**
 * LearningSuite group IDs are base64 Relay global IDs (R1 §4), e.g. `R3JvdXA6Y2…=` – standard
 * base64 may contain `+`, `/` and `=` padding (CR-WS7-3). Validity is checked via GET /groups/{id}
 * (`LearningSuiteGroupDirectory.checkGroup`, path segment URL-encoded).
 */
export const LearningSuiteGroupIdSchema = z
  .string()
  .trim()
  .min(1, 'Bitte ausfüllen.')
  .max(LEARNINGSUITE_GROUP_ID_MAX_LENGTH)
  .regex(/^[A-Za-z0-9+/=_-]+$/u, 'Ungültige LearningSuite-Gruppen-ID.');

export const SearchQuerySchema = z.string().trim().max(SEARCH_MAX_LENGTH).optional();

export const SortOrderSchema = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof SortOrderSchema>;

/** Query-string boolean (`true`/`false`/`1`/`0`). */
export const QueryBooleanSchema = z.stringbool();

/** Offset pagination for admin lists. Query strings are coerced. */
export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export function paginated<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().nonnegative(),
  });
}
export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

/** Empty JSON object (bodies of action endpoints without input). */
export const EmptyBodySchema = z.object({}).strict();

/** 202 acknowledgement without further data. */
export const AcceptedResponseSchema = z.object({ accepted: z.literal(true) });
export type AcceptedResponse = z.infer<typeof AcceptedResponseSchema>;

/** Seat counters of a company/batch (§34–§37). */
export const CodeStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  available: z.number().int().nonnegative(),
  pendingEmail: z.number().int().nonnegative(),
  claimed: z.number().int().nonnegative(),
  provisioning: z.number().int().nonnegative(),
  redeemed: z.number().int().nonnegative(),
  disabled: z.number().int().nonnegative(),
  /** Registrations in PROVISIONING_FAILED (code remains PROVISIONING). */
  failed: z.number().int().nonnegative(),
});
export type CodeStats = z.infer<typeof CodeStatsSchema>;
